import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import { toast } from 'sonner';
import { LogOut, Save, X, Camera, ArrowLeft, Lock } from 'lucide-react';
import ImageWithLoader from '@/components/ImageWithLoader';
import { useCache } from '@/context/CacheContext';
import { compressImage } from '@/lib/imageCompression';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('data:')) return path;
  return `${BACKEND_URL}${path}`;
};

export default function AccountCenter({ auth }) {
  const navigate = useNavigate();
  const { refreshProfile } = useCache();
  const [profilePicture, setProfilePicture] = useState(auth.user.profile_picture);
  const [profilePictureDate, setProfilePictureDate] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(auth.user.profile_picture);
  const [formData, setFormData] = useState({
    name: auth.user.name,
    surname: auth.user.surname,
    location: auth.user.location,
    gender: auth.user.gender
  });
  const [loading, setLoading] = useState(false);

  // Password Reset State
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const { base64, dateTaken } = await compressImage(file);
        setProfilePicture(base64);
        setProfilePictureDate(dateTaken);
        setProfilePicturePreview(base64);
      } catch (err) {
        toast.error("Failed to process image");
      }
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const updatePayload = {
        ...formData,
        profile_picture: profilePicture,
        profile_picture_date: profilePictureDate
      };

      await axios.put(`${API}/user/me`, updatePayload, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      toast.success('Profile updated successfully!');

      // Reload user data
      const response = await axios.get(`${API}/user/me`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      auth.login(auth.token, response.data);
      refreshProfile(); // Invalidate cache since profile changed
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.put(`${API}/user/me/password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });

      toast.success("Password updated successfully");
      setIsPasswordModalOpen(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50 p-4 pb-24">
      <div className="max-w-2xl mx-auto space-y-6">
        <Button
          variant="ghost"
          className="text-gray-600 hover:text-gray-900 -ml-2 h-8 mb-4"
          onClick={() => navigate('/profile')}
        >
          <ArrowLeft className="mr-2" size={16} />
          Back to Profile
        </Button>

        <Card className="p-6 glass">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Account Center</h2>

            <div className="flex items-center gap-4 mb-8">
              <div className="relative">
                <Avatar className="w-24 h-24">
                  {profilePicturePreview ? (
                    <ImageWithLoader
                      src={getImageUrl(profilePicturePreview)}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <AvatarFallback className="text-3xl">
                      {auth.user.name[0]}{auth.user.surname[0]}
                    </AvatarFallback>
                  )}
                </Avatar>

                <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                    className="hidden"
                    id="profile-picture-input"
                />
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="absolute bottom-0 right-0 rounded-full p-2 bg-white shadow-sm"
                    onClick={() => document.getElementById('profile-picture-input').click()}
                >
                    <Camera size={16} />
                </Button>
              </div>

              <div>
                  <h3 className="text-lg font-semibold">{auth.user.name} {auth.user.surname}</h3>
                  <p className="text-sm text-gray-500">{auth.user.email}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="name">First Name</Label>
                    <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1"
                    />
                </div>
                <div>
                    <Label htmlFor="surname">Last Name</Label>
                    <Input
                    id="surname"
                    name="surname"
                    value={formData.surname}
                    onChange={handleChange}
                    className="mt-1"
                    />
                </div>
              </div>

              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="gender">Gender</Label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                </select>
              </div>
            </div>

            <div className="flex gap-2 mt-8">
                  <Button
                    onClick={handleSave}
                    disabled={loading}
                    className="flex-1 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600"
                  >
                    <Save size={16} />
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
            </div>

             <div className="border-t pt-6 mt-8">
                <h3 className="font-semibold text-gray-800 mb-4">Security</h3>
                <Dialog open={isPasswordModalOpen} onOpenChange={setIsPasswordModalOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full flex items-center justify-center gap-2 mb-4">
                      <Lock size={16} />
                      Reset Password
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Reset Password</DialogTitle>
                      <DialogDescription>
                        Enter your current password and a new password to reset it.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="current-password">Current Password</Label>
                        <Input
                          id="current-password"
                          type="password"
                          value={passwordForm.currentPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-password">New Password</Label>
                        <Input
                          id="new-password"
                          type="password"
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm-password">Confirm New Password</Label>
                        <Input
                          id="confirm-password"
                          type="password"
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsPasswordModalOpen(false)}>Cancel</Button>
                      <Button onClick={handlePasswordReset} disabled={passwordLoading}>
                        {passwordLoading ? 'Updating...' : 'Update Password'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                 <div className="mb-6">
                    <h3 className="font-semibold text-gray-800 mb-2">Legal</h3>
                    <div className="flex flex-col gap-2">
                        <Button
                            variant="ghost"
                            className="justify-start h-auto py-2 px-3 text-gray-600 hover:text-teal-600 hover:bg-teal-50"
                            onClick={() => window.open('/terms', '_blank')}
                        >
                            Terms of Service
                        </Button>
                        <Button
                            variant="ghost"
                            className="justify-start h-auto py-2 px-3 text-gray-600 hover:text-teal-600 hover:bg-teal-50"
                            onClick={() => window.open('/privacy', '_blank')}
                        >
                            Privacy Policy
                        </Button>
                    </div>
                 </div>

                 <Button
                    onClick={auth.logout}
                    variant="destructive"
                    className="w-full flex items-center justify-center gap-2 mt-4"
                >
                    <LogOut size={16} />
                    Logout
                </Button>
             </div>
        </Card>

        <div className="flex justify-center mt-8">
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogTrigger asChild>
                    <Button variant="link" className="text-xs text-gray-400 hover:text-gray-500">
                        Delete Account
                    </Button>
                </DialogTrigger>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Account?</DialogTitle>
                        <DialogDescription>
                            This action cannot be undone. This will permanently delete your account, including all your entries, likes, comments, and profile data.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end gap-3 mt-4">
                        <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={async () => {
                                try {
                                    await axios.delete(`${API}/user/me`, {
                                        headers: { Authorization: `Bearer ${auth.token}` }
                                    });
                                    auth.logout();
                                    toast.success("Account deleted successfully");
                                } catch (error) {
                                    toast.error("Failed to delete account");
                                }
                            }}
                        >
                            Yes, Delete My Account
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
      </div>
    </div>
  );
}
